import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import OrgExpenseReportSummaryBar from 'src/components/OrgExpenseReportSummaryBar/OrgExpenseReportSummaryBar'
import ReportTransactionsBoxAndLinks from 'src/components/ReportTransactionsBoxAndLinks/ReportTransactionsBoxAndLinks'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import StatementSummaryHeader from 'src/components/StatementSummaryHeader/StatementSummaryHeader'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { IssuerCodes, TransactionFilterOptions } from 'src/enums'
import {
  useBackgroundDispatch,
  useChangedEffect,
  useEventListener,
  usePrevious,
} from 'src/hooks'
import { useQueryCreditCard } from 'src/queries/creditCard.queries'
import { useQueryOrgExpenseReport } from 'src/queries/orgExpenseReports.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { updateSelectedTransactions } from 'src/redux/actions/data/transactions.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectExpenseReportIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'

import styles from './Statement.style'

const OrgExpenseReport: React.FC = () => {
  const { show_automated_expense_review: showAutomatedExpenseReview } =
    useSelector(selectFeatureFlaggerFromState)

  const reportId = useSelector(selectExpenseReportIdFromRoute)
  const { data: report, error: fetchFailure } = useQueryOrgExpenseReport({
    reportId,
  })
  const reportPrevious = usePrevious(report)
  const user = useSelector(selectUserListModelFromState)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const queryClient = useQueryClient()
  const { data: creditCard } = useQueryCreditCard({
    creditCardId: report?.accountId,
    retry: true,
  })

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })
  const clientHeight = useMemo(
    () => viewportHeight.height - 122,
    [viewportHeight],
  )

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEventListener('resize', updateWindowSize)

  const dispatch = useBackgroundDispatch()

  const [initialLoad, setInitialLoad] = useState(true)

  // listen for changes to report close date to re-fetch transactions
  useChangedEffect(() => {
    if (reportPrevious && reportPrevious?.until !== report?.until) {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
    }
  }, [reportPrevious, report])

  // clear selected transactions on first render
  useEffect(() => {
    dispatch(updateSelectedTransactions())
  }, [])

  const [externallyAppliedFilters, setExternallyAppliedFilters] = useState({})

  return user?.hasOrgBookkeeperPermissions && !fetchFailure ? (
    <BackgroundPage className="Statement" css={styles}>
      <BackgroundPageContent noStackingIndex>
        {showAutomatedExpenseReview && (
          <StatementSummaryHeader report={report} creditCard={creditCard} />
        )}
        {!showAutomatedExpenseReview && (
          <OrgExpenseReportSummaryBar
            setInitialLoad={setInitialLoad}
            creditCard={creditCard}
            setExternallyAppliedFilters={setExternallyAppliedFilters}
          />
        )}
        <ReportTransactionsBoxAndLinks
          className="Statement transactions"
          context={TransactionReportType.ExpenseReport}
          showIntegrationsLink
          showConnection
          design={TransactionsTableLargeDesigns.UserAndCardAndStatus}
          hasReceiptToolsDropdown
          initialLoad={initialLoad}
          setInitialLoad={setInitialLoad}
          canViewAllTransactions={false}
          reportId={reportId}
          externallyAppliedFilters={externallyAppliedFilters}
          visibleFilters={[
            ...// using is issuer Regions check until BE updates Regions cards to accurately set physicalClearingsEnabled
            (organization?.physicalClearingsEnabled ||
            [IssuerCodes.Regions, IssuerCodes.RegionsTest].includes(
              creditCard?.issuer as IssuerCodes,
            )
              ? [TransactionFilterOptions.TransactionOrigin]
              : []),
            TransactionFilterOptions.ExpenseCategoryStatus,
            TransactionFilterOptions.ExpenseCategoryLabels,
            ...(receiptManagementEnabled !== undefined
              ? [TransactionFilterOptions.ReceiptStatus]
              : []),
            ...(activeIntegration !== undefined &&
            user?.hasOrgBookkeeperPermissions
              ? [
                  TransactionFilterOptions.TransactionSyncStatus,
                  TransactionFilterOptions.ReceiptSyncStatus,
                ]
              : []),
            TransactionFilterOptions.CardUser,
            TransactionFilterOptions.Departments,
            TransactionFilterOptions.MerchantCategory,
            TransactionFilterOptions.ReviewStatus,
          ]}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </BackgroundPage>
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default OrgExpenseReport
